import { useEffect } from 'react';
import {
  Box,
  Button,
  Container,
  Flex,
  Grid,
  Heading,
  HStack,
  Icon,
  Image,
  SimpleGrid,
  Text,
  VStack,
  useColorModeValue,
} from '@chakra-ui/react';
import { BrowserRouter, Routes, Route, Navigate, Link as RouterLink } from 'react-router-dom';
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import ScrollToTop from './components/ScrollToTop';
import PageMeta from './components/PageMeta';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsOfService from './pages/TermsOfService';
import RefundPolicy from './pages/RefundPolicy';
import Subprocessors from './pages/Subprocessors';
import ResponsibleGaming from './pages/ResponsibleGaming';
import Contact from './pages/Contact';
import ClaimProfile from './pages/ClaimProfile';
import AboutUs from './pages/AboutUs';
import BlogIndex from './pages/blog/BlogIndex';
import BlogPost from './pages/blog/BlogPost';
import {
  HeroRatingsTeaserLite,
  MarketingRatingsProvider,
  PreviewSectionDivider,
  PreviewSectionPanel,
  ProductPreviewSection,
  PreviewQuickNav,
  useMarketingRatings,
} from '@ww/marketing-preview';
import { HOMEPAGE, MEET_PR, MEET_WCS, PAGE_META } from './content/brandMessaging';
import { ABOUT_PILLARS } from './content/aboutPillars';
import {
  SIGNUP_URL,
  WW_API_BASE_URL,
} from './config/api';
import RatingsPage from './pages/Ratings';
import { warmAppNavigationLinks } from './utils/appLinkHints';

const APP_URL = 'https://app.wagerwatchdog.com';

function Hero() {
  const { heroRows, isLoading } = useMarketingRatings();

  return (
    <Box
      pt={{ base: 8, md: 12 }}
      pb={{ base: 16, md: 22 }}
      position="relative"
      overflow="hidden"
    >
      <Box
        position="absolute"
        top="-300px"
        left="50%"
        transform="translateX(-50%)"
        w="1200px"
        h="1200px"
        bg="radial-gradient(ellipse at center, rgba(10,112,185,0.18) 0%, rgba(10,112,185,0.06) 40%, transparent 70%)"
        pointerEvents="none"
        zIndex={0}
      />
      <Box
        position="absolute"
        bottom={0}
        left={0}
        right={0}
        h={{ base: '120px', md: '160px' }}
        bgGradient="linear(to-b, transparent, gray.900)"
        pointerEvents="none"
        zIndex={1}
      />
      <Container maxW="container.xl" position="relative" zIndex={2} px={{ base: 4, md: 6 }}>
        <Grid
          templateColumns={{ base: '1fr', lg: 'minmax(0, 1.15fr) minmax(0, 0.85fr)' }}
          gap={{ base: 10, lg: 12, xl: 14 }}
          alignItems="center"
        >
          <VStack
            spacing={6}
            textAlign={{ base: 'center', lg: 'left' }}
            align={{ base: 'center', lg: 'start' }}
            justify="center"
            w="100%"
            minW={0}
          >
            <VStack spacing={4} align={{ base: 'center', lg: 'start' }} w="100%">
              <Heading
                as="h1"
                fontSize={{ base: '3xl', md: '4xl', lg: '4xl', xl: '5xl' }}
                fontWeight="extrabold"
                lineHeight="1.12"
                color="white"
                maxW="100%"
                sx={{ textWrap: 'balance' }}
              >
                {HOMEPAGE.title}
                {HOMEPAGE.titleAccent ? (
                  <>
                    {' '}
                    <Text as="span" color="primary.400">
                      {HOMEPAGE.titleAccent}
                    </Text>
                  </>
                ) : null}
              </Heading>
              <Text
                fontSize={{ base: 'lg', md: 'xl' }}
                color="gray.300"
                fontWeight="medium"
                maxW="100%"
                lineHeight="1.5"
                sx={{ textWrap: 'pretty' }}
              >
                {HOMEPAGE.hook}
              </Text>
              <Text
                fontSize={{ base: 'md', md: 'lg' }}
                color="gray.400"
                maxW="100%"
                lineHeight="1.7"
                sx={{ textWrap: 'pretty' }}
              >
                {HOMEPAGE.subtitle}
              </Text>
            </VStack>
            <HStack
              spacing={4}
              flexWrap="wrap"
              justify={{ base: 'center', lg: 'start' }}
              mb={{ base: 6, md: 8 }}
              pb={{ base: 2, md: 0 }}
            >
              <Button
                as={RouterLink}
                to="/ratings"
                variant="primary"
                size="lg"
                px={8}
                _hover={{ textDecoration: 'none' }}
              >
                {HOMEPAGE.primaryCta}
              </Button>
              <Button
                as={RouterLink}
                to="/claim"
                variant="outline"
                size="lg"
                px={8}
                borderColor="whiteAlpha.300"
                color="white"
                _hover={{ textDecoration: 'none', bg: 'whiteAlpha.100' }}
              >
                {HOMEPAGE.secondaryCta}
              </Button>
            </HStack>
          </VStack>
          <Flex w="100%" align="center" justify="center">
            <HeroRatingsTeaserLite rows={heroRows} isLoading={isLoading} />
          </Flex>
        </Grid>
      </Container>
    </Box>
  );
}

function Features() {
  const watchdogDogSrc = useColorModeValue(
    '/assets/images/watchdog_logo_dog_blue.svg',
    '/assets/images/watchdog_logo_dog_white.svg',
  );
  const pillarBg = useColorModeValue('gray.50', 'blackAlpha.350');
  const pillarBorder = useColorModeValue('gray.200', 'whiteAlpha.120');

  return (
    <Box
      id="features"
      pt={{ base: 4, md: 6 }}
      pb={{ base: 10, md: 16 }}
    >
      <Container maxW="container.content">
        <PreviewSectionDivider />
        <PreviewSectionPanel
          eyebrow="Performance over popularity"
          eyebrowTone="primary"
          title="Independent ratings you can verify"
          description="Thousands of sports betting experts publish picks every day. We measure how they actually perform—not how loud they market themselves."
          headerBadge={{ label: 'Our mission', colorScheme: 'blue' }}
        >
          <VStack spacing={{ base: 8, md: 10 }} align="stretch">
            <Image
              src={watchdogDogSrc}
              alt=""
              aria-hidden
              h={{ base: '112px', md: '140px' }}
              w="auto"
              maxW="min(400px, 92vw)"
              mx="auto"
              objectFit="contain"
              display="block"
              sx={{
                filter: 'drop-shadow(0 10px 28px rgba(0, 0, 0, 0.45))',
                transform: 'translateY(-6px)',
              }}
            />
            <SimpleGrid columns={{ base: 1, md: 3 }} spacing={5} w="full">
              {ABOUT_PILLARS.map((pillar) => (
                <Box
                  key={pillar.title}
                  p={{ base: 6, md: 7 }}
                  bg={pillarBg}
                  borderRadius="12px"
                  border="1px solid"
                  borderColor={pillarBorder}
                  borderLeftWidth="3px"
                  borderLeftColor={pillar.color}
                  minH={{ base: 'auto', md: '220px' }}
                  transition="all 0.2s"
                  _hover={{
                    borderColor: pillar.color,
                    transform: 'translateY(-2px)',
                    boxShadow: '0 12px 32px -16px rgba(0, 0, 0, 0.55)',
                  }}
                >
                  <VStack align="start" spacing={4}>
                    <Icon viewBox="0 0 24 24" boxSize={8} color={pillar.color}>
                      <path fill="currentColor" d={pillar.iconPath} />
                    </Icon>
                    <Heading as="h3" size="md" color="white">
                      {pillar.title}
                    </Heading>
                    <Text color="gray.400" lineHeight="1.7" fontSize="sm">
                      {pillar.body}
                    </Text>
                  </VStack>
                </Box>
              ))}
            </SimpleGrid>
            <HStack
              spacing={4}
              justify="center"
              flexWrap="wrap"
              pt={2}
              pb={{ base: 2, md: 4 }}
            >
              <Button
                as={RouterLink}
                to="/about"
                variant="primary"
                size="lg"
                px={8}
                _hover={{ textDecoration: 'none' }}
              >
                About Us
              </Button>
              <Button
                as={RouterLink}
                to="/contact"
                variant="outline"
                size="lg"
                px={8}
                borderColor="whiteAlpha.300"
                color="white"
                _hover={{ textDecoration: 'none', bg: 'whiteAlpha.100' }}
              >
                Support
              </Button>
            </HStack>
          </VStack>
        </PreviewSectionPanel>
      </Container>
    </Box>
  );
}

function LandingPage() {
  return (
    <Box minH="100vh" bg="gray.900" w="100%" maxW="100%">
      <PageMeta
        fullTitle={PAGE_META.title}
        description={PAGE_META.description}
      />
      <Navbar />
      <Hero />
      <PreviewQuickNav />
      <ProductPreviewSection
        signupUrl={SIGNUP_URL}
        ratingsUrl="/ratings"
        meetWcs={MEET_WCS}
        meetPr={MEET_PR}
        expertRatingsMaxRows={10}
      />
      <Features />
      <Footer />
    </Box>
  );
}

function App() {
  useEffect(() => {
    warmAppNavigationLinks(APP_URL);
  }, []);

  return (
    <MarketingRatingsProvider
      apiBaseUrl={WW_API_BASE_URL}
      appBaseUrl={APP_URL}
    >
      <BrowserRouter>
        <ScrollToTop />
        <Routes>
          <Route path="/" element={<LandingPage />} />
          <Route path="/ratings" element={<RatingsPage />} />
          <Route path="/ratings/" element={<RatingsPage />} />
        <Route path="/privacy" element={<PrivacyPolicy />} />
        <Route path="/privacy/" element={<PrivacyPolicy />} />
        <Route path="/terms" element={<TermsOfService />} />
        <Route path="/terms/" element={<TermsOfService />} />
        <Route path="/refunds" element={<RefundPolicy />} />
        <Route path="/refunds/" element={<RefundPolicy />} />
        <Route path="/subprocessors" element={<Subprocessors />} />
        <Route path="/subprocessors/" element={<Subprocessors />} />
        <Route path="/responsible-gaming" element={<ResponsibleGaming />} />
        <Route path="/responsible-gaming/" element={<ResponsibleGaming />} />
        <Route
          path="/responsible-gambling"
          element={<Navigate to="/responsible-gaming" replace />}
        />
        <Route
          path="/responsible-gambling/"
          element={<Navigate to="/responsible-gaming" replace />}
        />
        <Route path="/contact" element={<Contact />} />
        <Route path="/contact/" element={<Contact />} />
        <Route path="/claim" element={<ClaimProfile />} />
        <Route path="/claim/" element={<ClaimProfile />} />
        <Route path="/about" element={<AboutUs />} />
        <Route path="/about/" element={<AboutUs />} />
        <Route path="/about-us" element={<Navigate to="/about" replace />} />
        <Route path="/about-us/" element={<Navigate to="/about" replace />} />
        <Route path="/blog" element={<BlogIndex />} />
        <Route path="/blog/" element={<BlogIndex />} />
        <Route path="/blog/:slug" element={<BlogPost />} />
        <Route path="/blog/:slug/" element={<BlogPost />} />
        <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </MarketingRatingsProvider>
  );
}

export default App;
